import React, { useState, useEffect } from "react";
import { Table, Form, OverlayTrigger, Tooltip } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchWeeklyTimesheets,
  saveDayEntry,
  showGeneralError,
  createOrUpdateWeeklyTimesheet,
  saveComment,
  deleteWeeklyTimesheetEntry,
} from "actions";
import AddCommentModal from "../AddCommentModal";
import { Filter, Header } from "../";
import { isEmpty, set } from "lodash";
import moment from "moment-timezone";
import TimeInputCustom from "../TimeInputCustom";
import { useLocation } from "react-router-dom";
import { DeleteModal } from "CommonComponent";

const List = () => {
  const dispatch = useDispatch();
  const location = useLocation();

  const session = useSelector((s) => s.session);
  const weeklyTimesheetState = useSelector(
    (state) => state.weeklyTimesheetState
  );
  const object_viewed_id = session?.current_page_permissions?.object_id || "";

  const [data, setData] = useState([]);
  const [extraFiltersState, setExtraFiltersState] = useState({
    values: {},
  });

  const [days, setDays] = useState([]);
  const [dates, setDates] = useState([]);
  const [commentItem, setCommentItem] = useState(null);
  const [currentWeek, setCurrentWeek] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);
  const [weekValue, setWeekValue] = useState(null);

  useEffect(() => {
    dispatch({ type: "CLEAR_WEEKLY_TIMESHEETS" }); // Clear the timesheet data
    setDates([]);
    setDays([]);
  }, [extraFiltersState.values.week_end_date]);

  useEffect(() => {
    if (
      weeklyTimesheetState.redirectToList &&
      session.current_page_permissions.page_url === location.pathname
    ) {
      filterTable();
    }
  }, [weeklyTimesheetState.redirectToList, session.current_page_permissions]);

  useEffect(() => {
    if (
      currentWeek &&
      session.current_page_permissions.page_url === location.pathname
    ) {
      filterTable();
    }
  }, [currentWeek, session.current_page_permissions]);

  useEffect(() => {
    const weeklyTimesheets = weeklyTimesheetState?.weeklyTimesheetsList;

    // 🔐 HARD GUARD
    if (!Array.isArray(weeklyTimesheets)) {
      setData([]);
      return;
    }

    const formattedData = [];

    weeklyTimesheets.forEach((programData) => {
      if (!Array.isArray(programData?.tasks)) return;

      let isFirstTaskOfProgram = true;

      programData.tasks.forEach((taskItem) => {
        const processEntry = (entry, isOverTime) => {
          if (!entry) return;

          const dailyValues = Array.from({ length: 7 }, () => ({
            hours: "",
            minutes: "",
          }));

          if (Array.isArray(entry.details)) {
            entry.details.forEach((detail) => {
              const dayIndex = new Date(
                detail.work_date + "T00:00:00"
              ).getDay();

              const adjustedIndex = dayIndex === 0 ? 6 : dayIndex - 1;

              if (adjustedIndex >= 0 && adjustedIndex < 7) {
                dailyValues[adjustedIndex] = {
                  hours: String(detail.hours ?? ""),
                  minutes: String(detail.minutes ?? ""),
                };
              }
            });
          }

          formattedData.push({
            program: isFirstTaskOfProgram
              ? programData.program?.program_name || ""
              : "",
            category: taskItem.task?.category?.name || "N/A",
            task: taskItem.task?.name || "",
            values: dailyValues,
            program_id: programData.program?.id || null,
            task_id: taskItem.task?.id || null,
            category_id: taskItem.task?.category?.id || null,
            weekly_timesheet_id: entry.id,
            total: parseFloat(entry.total_hours || 0),
            is_over_time_entry: isOverTime,
            comment: entry.comments || "",
          });

          isFirstTaskOfProgram = false;
        };

        processEntry(taskItem.main_entry, false);
        processEntry(taskItem.overtime_entry, true);
      });
    });

    setData(formattedData);
  }, [weeklyTimesheetState.weeklyTimesheetsList]);

  const filterTable = () => {
    if (!isEmpty(extraFiltersState.values.week_end_date)) {
      const weekEndDate = moment(extraFiltersState.values.week_end_date);
      const weekStartDate = weekEndDate.clone().subtract(6, "days");
      const newDates = [];
      const newDays = [];
      let currentDate = weekStartDate;
      while (currentDate.isSameOrBefore(weekEndDate)) {
        newDates.push(currentDate.format("M/DD"));
        newDays.push(currentDate.format("ddd").toUpperCase());
        currentDate.add(1, "days");
      }
      setDates(newDates);
      setDays(newDays);

      dispatch(
        fetchWeeklyTimesheets({
          ...extraFiltersState.values,
          object_viewed_id,
        })
      );
    }
  };

  const handleValueChange = (rowIndex, dayIndex, { field, value }) => {
    const updatedData = [...data];
    updatedData[rowIndex].values[dayIndex][field] = value;

    // calculate total hours
    updatedData[rowIndex].total = updatedData[rowIndex].values.reduce(
      (sum, v) => {
        const hours = parseInt(v.hours || 0);
        const minutes = parseInt(v.minutes || 0);
        return sum + hours + minutes / 60;
      },
      0
    );

    setData(updatedData);
  };

  const handleShiftTimeUpdate = (rowIndex, dayIndex) => {
    const { hours, minutes } = data[rowIndex].values[dayIndex];
    const item = data[rowIndex];

    // Calculate the work date based on dayIndex and week_end_date
    if (!extraFiltersState.values.week_end_date) {
      showGeneralError("Week end date is required to save day entry");
      return;
    }

    const weekEndDate = moment(extraFiltersState.values.week_end_date);
    const weekStartDate = weekEndDate.clone().subtract(6, "days");
    const workDate = weekStartDate.clone().add(dayIndex, "days");

    // Calculate total hours (hours + minutes/60)
    const hoursValue =
      hours === "" || hours === null || hours === undefined
        ? 0
        : parseFloat(hours || 0);
    const minutesValue =
      minutes === "" || minutes === null || minutes === undefined
        ? 0
        : parseFloat(minutes || 0);
    const totalHours = hoursValue + minutesValue / 60;

    // Prepare data for API
    const dayEntryData = {
      weekly_timesheet_id: item.weekly_timesheet_id, // You'll need to add this to your data structure
      work_date: workDate.format("YYYY-MM-DD"),
      total_time: totalHours,
      minutes: minutesValue,
      hours: hoursValue,
    };

    if (!item.weekly_timesheet_id) {
      showGeneralError("Weekly timesheet ID is required to save day entry");
      return;
    }

    dispatch(
      saveDayEntry(item.weekly_timesheet_id, dayEntryData, object_viewed_id)
    );
  };

  const addOverTime = (e, item) => {
    e.preventDefault();
    const overTimeData = {
      program_id: item.program_id,
      task_ids: [item.task_id],
      is_over_time_entry: true,
      week_end_date: extraFiltersState.values.week_end_date,
    };
    dispatch(
      createOrUpdateWeeklyTimesheet(overTimeData, "POST", object_viewed_id)
    );
  };

  const handleAddComment = (comment) => {
    if (commentItem) {
      const commentData = { comment: comment };
      dispatch(
        saveComment(
          commentItem.weekly_timesheet_id,
          commentData,
          object_viewed_id
        )
      )
        .then(() => {
          const updatedData = data.map((item) => {
            if (item.weekly_timesheet_id === commentItem.weekly_timesheet_id) {
              return { ...item, comment: comment };
            }
            return item;
          });
          setData(updatedData);
          setShowComment(false);
          setCommentItem(null);
        })
        .catch(() => {
          // Error is already handled by the action
        });
    }
  };

  const [showComment, setShowComment] = useState(false);

  const handleDelete = (item) => {
    setDeleteItem(item);
    setShowDeleteModal(true);
  };

  const confirmDelete = () => {
    if (!deleteItem) return;
    dispatch(
      deleteWeeklyTimesheetEntry(
        deleteItem.weekly_timesheet_id,
        object_viewed_id
      )
    )
      .then(() => {
        setShowDeleteModal(false);
        setDeleteItem(null);
        // Refresh the table after deletion
        if (!isEmpty(extraFiltersState.values.week_end_date)) {
          filterTable();
        }
      })
      .catch(() => {
        // Error is already handled by the action
        setShowDeleteModal(false);
        setDeleteItem(null);
      });
  };

  const hasTimeValue = (value) => {
    return (
      (value.hours && value.hours !== "") ||
      (value.minutes && value.minutes !== "")
    );
  };

  
  return (
    <div className="wrapper-div">
      <Header
        extraFiltersState={extraFiltersState}
        setCurrentWeek={setCurrentWeek}
        setExtraFiltersState={setExtraFiltersState}
      />
      <div>
        <Filter
          onFilter={filterTable}
          extraFiltersState={extraFiltersState}
          setExtraFiltersState={setExtraFiltersState}
          currentWeek={currentWeek}
          setCurrentWeek={setCurrentWeek}
          weekValue={weekValue}
          setWeekValue={setWeekValue}
        />
      </div>
      <div className="weekly-table-container td-border">
        <Table className="no-border">
          <thead>
            <tr>
              <th>Program Name</th>
              <th>Category</th>
              <th>Task</th>
              {dates.map((date, index) => (
                <th key={index}>
                  {date} <br /> <span className="fw-bold">{days[index]}</span>
                </th>
              ))}
              <th>Weekly Total</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {data.map((item, rowIndex) => (
              <tr
                key={rowIndex}
                className={item.is_over_time_entry ? "over-time-entry" : ""}
              >
                <td className="program-name">{item.program}</td>
                <td>{item.category}</td>
                <td>{item.task}</td>
                {item.values.map((value, dayIndex) => (
                  <td key={dayIndex}>
                    <TimeInputCustom
                      value={value}
                      rowIndex={rowIndex}
                      dayIndex={dayIndex}
                      handleValueChange={handleValueChange}
                      handleShiftTimeUpdate={handleShiftTimeUpdate}
                      hasTime={hasTimeValue(value)}
                    />
                  </td>
                ))}
                <td>{item.total}</td>
                <td>
                  <div className="d-flex align-items-center">
                    {!item.is_over_time_entry ? (
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip id={`overtime-tooltip-${rowIndex}`}>
                            Add Overtime
                          </Tooltip>
                        }
                      >
                        <img
                          src="/assets/images/time-icon.svg"
                          alt="Add Over Time"
                          className="me-2"
                          style={{ cursor: "pointer" }}
                          onClick={(e) => addOverTime(e, item)}
                        />
                      </OverlayTrigger>
                    ) : null}
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip id={`comment-tooltip-${rowIndex}`}>
                          {item.comment ? item.comment : "Add Comment"}
                        </Tooltip>
                      }
                    >
                      <img
                        src="/assets/images/comment-icon.svg"
                        alt="Comment"
                        onClick={() => {
                          setCommentItem(item);
                          setShowComment(true);
                        }}
                        style={{ cursor: "pointer" }}
                      />
                    </OverlayTrigger>

                    {/* Delete – Corrected */}
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip id={`delete-tooltip-${rowIndex}`}>
                          Delete Entry
                        </Tooltip>
                      }
                    >
                      <img
                        src="/assets/images/delete-icon.svg"
                        alt="Delete"
                        onClick={() => handleDelete(item)}
                        style={{ cursor: "pointer" }}
                      />
                    </OverlayTrigger>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>

        {/* comment modal */}
        <AddCommentModal
          show={showComment}
          handleClose={() => {
            setShowComment(false);
            setCommentItem(null);
          }}
          taskComment={commentItem?.comment || ""}
          handleAdd={handleAddComment}
          dates={dates}
        />
      </div>

      {/* delete confirmation modal */}
      {showDeleteModal && (
        <DeleteModal
          show={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false);
            setDeleteItem(null);
          }}
          onDelete={confirmDelete}
          message="Are you sure to delete this timesheet entry?"
        />
      )}
    </div>
  );
};

export default List;
